import React, { useState, useReducer, useEffect } from 'react'
import { useSnackbar } from 'notistack';
import { makeStyles } from '@material-ui/core/styles';
import TMForm from '../lib/Form/TMForm';
import client from '../feathers';
import IconButton from '@material-ui/core/IconButton';
import CreateIcon from '@material-ui/icons/AddBox';
import EditIcon from '@material-ui/icons/Edit';
import Switch from '@material-ui/core/Switch';
import useIntl from '../hooks/useIntl';
import TMDataTable from '../lib/Table/TMDataTable';
import TMDialog from '../lib/Dialog/TMDialog';
import { useProgressDialog } from '../context/ProgressDialogProvider';
import { useAlertDialog } from '../context/AlertDialogProvider';
import useConfirmDialog from '../hooks/useConfirmDialog';
import useSentry from '../hooks/useSentry';
import useAuth from '../hooks/useAuth';
import AddTransporterDialog from '../components/releases-transporters/AddTransporterDialog.jsx';
import BarChartIcon from '@material-ui/icons/BarChart';
import { useSuccessRateOfSecurePickup } from '../hooks/useSuccessRateOfSecurePickup';

const useStyles = makeStyles((theme) => ({
  hoverButton: {
    color: '#757575',
    '&:hover': {
    	color: theme.palette.primary.main
    }
  },
  inactive: {
    '& td': {
      color: theme.palette.secondary.main,
    }
  },
}));

export default function TrainsOverview() {
	
	const [openAdd, setOpenAdd] = useState(false);
	const [openEdit, setOpenEdit] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState);
  const { showProgressDialog, hideProgressDialog } = useProgressDialog();
  const { showAlert } = useAlertDialog();
  const { enqueueSnackbar, closeSnackbar } = useSnackbar();
	const { translate } = useIntl();
	const classes = useStyles();
	const logSentry = useSentry();
  const { user } = useAuth();
  const  { successRateOfSecurePickupJsx, openSrsPickupDialog } = useSuccessRateOfSecurePickup('trains');

	const fetchTrains = async () => {
		const result = await client.service('trains').find();
	  dispatch({ payload: { trains: result.data } })
  };

  useEffect(() => {
    fetchTrains();
  }, []);

	const columns = [
		{ name: 'Name' },
		{ name: 'Train code' },
		{ name: 'Action', options: { filter: false, download: false, viewColumns: false } },
		{ name: "isActive", options: { filter: false, download: false, display: false, viewColumns: false } }
	];

	const openEditTrain = async (train) => {
    dispatch({ payload: {
    	name: train.name,
		  identification: train.identification,
		  isActive: train.isActive,
			organizationAddress: train.organization_address,
			id: train.id,
    }})
    setOpenEdit(true);
  };

  const handleDialogClose = async () => {
    await fetchTrains();
    dispatch({ type: 'RESET' });
  	openEdit ? setOpenEdit(false) : setOpenAdd(false);
  }

	const handleChange = (e) => {
    dispatch({ payload: { 
    	[e.target.name]: (e.target.hasOwnProperty('checked') ? e.target.checked : e.target.value) 
    }})
  };

  const checkEdit = async () => {
  	const currentTrain = state.trains.find(t => t.id === state.id);
  	if ((!state.isActive && currentTrain.isActive) || (state.identification !== currentTrain.identification)) {
  		showConfirmDialog('');
  	} else {
  		handleEditTrain();
  	}
  }

	const handleEditTrain = async () => {
    let didNotError = true;
    try {
    	showProgressDialog();
			const data = {
				name: state.name,
				trainCode: state.identification,
				isActive: state.isActive
			}
			const result = await client.service('trains').patch(state.id, data);
    	const currentTrain = state.trains.find(t => t.id === state.id);

    	// check if train was inactivated or traincode changed
    	if ((!state.isActive && currentTrain.isActive) || (state.identification !== currentTrain.identification)) {
    		enqueueSnackbar(translate('train.edit.warning.unassigning'), { persist: true });
    		await window.walletApi.unassignReleases(
          result.removedReleases.map(({release}) => ({ address: release.address, version: release.version })),
          { from: user.organization }
        );
      	closeSnackbar();
    	}
  		enqueueSnackbar(translate('train.patched'), { autoHideDuration: 1500 });
    } catch (error) {
      logSentry(error);
    	showAlert(error.message);
    	didNotError = false;
    } finally {
      didNotError && await fetchTrains();
      handleDialogClose();
      hideProgressDialog();
    }
	}

	const { showConfirmDialog } = useConfirmDialog(
		'train.edit', 'train.edit.warning', 
		handleEditTrain, handleDialogClose,
		'general.ok', 'general.cancel'
	)

	const setRowProps = (row, dataIndex) => {
    // last field = isActive
    if(row[row.length-1] === false) return { className: classes.inactive }
  };

	const getData = () => {
		return state.trains.map(t => [
			t.name, 
			t.identification, 
			<IconButton className={classes.hoverButton} onClick={() => openEditTrain(t)}><EditIcon/></IconButton>,
			t.isActive
		])
	}

	return (
		<React.Fragment>
			<div className="trains-table">
				<TMDataTable
		      title={'Trains'}
		      columns={columns}
		      data={getData()}
		      setRowProps={setRowProps}
		      options={{
			      print: false,
			      filter: false,
			      viewColumns: false,
				  customToolbar: () => (
					<React.Fragment>
					  <IconButton
						className={classes.hoverButton}
						onClick={() => openSrsPickupDialog()}
						>
						<BarChartIcon />
					  </IconButton>
					  <IconButton
					  className={classes.hoverButton}
					  onClick={() => setOpenAdd(true)}
					  >
					  <CreateIcon />
					  </IconButton>
					</React.Fragment>
				),
			     
		      }}
		    />
	    </div>

			<AddTransporterDialog
				type={'train'} 
				openAdd={openAdd} 
				handleDialogClose={handleDialogClose}
			/>

	    <TMDialog
	      key="edit-train"
	      title={"Edit train"}
	      dialogOpen={openEdit}
	      handleDialogClose={handleDialogClose}
	      maxWidth="md"
	      showBottomClose={false}
	    >
	      <TMForm
	        object={state}
	        handleChange={handleChange}
	        handleSubmit={checkEdit}
	        submitLabel={translate("train.edit")}
	        handleCancel={handleDialogClose}
	        cancelLabel={translate("general.cancel")}
	        fields={[
	    			{ field: 'name', label: 'Name', required: true }, 
	    			{ field: 'identification', label: 'Train code', required: true }, 
            { field: 'isActive', label: 'Active ?', componentType: Switch },
	        ]}
	      />
	    </TMDialog>
		{ successRateOfSecurePickupJsx }
		</React.Fragment>
	);
}

const initialState = {
  trains: [],
  name: '',
  identification: '',
  isActive: false,
  organizationAddress: '',
  id: 0,
}

const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'RESET':
      return { ...initialState, trains: state.trains }
    default:
      // = SET
      return { ...state, ...payload }
  }
}
